import { CreditCard, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/badge';
import { Card } from '@/components/card';
import { EmptyState } from '@/components/empty-state';
import {
  intervalLabel,
  subscriptionStatusLabel,
  subscriptionStatusTone,
  useClientSubscriptions,
} from '@/lib/subscriptions';
import { dollars, formatDate } from '@/lib/format';
import type { SubscriptionData } from '@/types/api';

/**
 * Read-only mirror of the client's Stripe subscriptions. Subscriptions are
 * created and managed in Stripe; this view is for revenue visibility only.
 */
export function ClientSubscriptions({ clientId }: { clientId: number }) {
  const { data: subs, isLoading } = useClientSubscriptions(clientId);
  const list = subs ?? [];

  const activeMrr = list
    .filter((s) => s.counts_toward_mrr)
    .reduce((sum, s) => sum + s.monthly_equivalent_cents, 0);
  const activeCount = list.filter((s) => s.counts_toward_mrr).length;

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <div className="grid grid-cols-3 divide-x divide-[var(--color-border)] text-center">
          <Stat label="Monthly (MRR)" value={dollars(activeMrr)} />
          <Stat label="Active" value={activeCount.toString()} />
          <Stat label="Total" value={list.length.toString()} />
        </div>
      </Card>

      <div className="flex items-center justify-between">
        <p className="text-xs text-[var(--color-foreground-muted)]">
          Managed in Stripe. Changes sync here automatically.
        </p>
        <a
          href="https://dashboard.stripe.com/subscriptions"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--color-primary)] hover:underline"
        >
          Manage in Stripe <ExternalLink className="size-3.5" />
        </a>
      </div>

      {isLoading ? (
        <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>
      ) : list.length === 0 ? (
        <EmptyState
          icon={CreditCard}
          title="No subscriptions"
          description="Create a subscription for this client in Stripe. It will appear here once it syncs."
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {list.map((sub) => (
            <SubscriptionCard key={sub.id} sub={sub} />
          ))}
        </div>
      )}
    </div>
  );
}

function SubscriptionCard({ sub }: { sub: SubscriptionData }) {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-start justify-between gap-3 border-b border-[var(--color-border)] px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="truncate font-semibold">{sub.display_name}</h3>
            <Badge tone={sub.cancel_at_period_end ? 'warning' : subscriptionStatusTone(sub.status)}>
              {subscriptionStatusLabel(sub)}
            </Badge>
          </div>
          <div className="mt-0.5 font-mono text-[11px] text-[var(--color-foreground-subtle)]">
            {sub.stripe_subscription_id}
          </div>
        </div>
        <div className="text-right">
          <div className="font-semibold tabular-nums">{dollars(sub.amount_cents)}</div>
          <div className="text-xs text-[var(--color-foreground-muted)]">
            {intervalLabel(sub.interval, sub.interval_count)}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 divide-x divide-[var(--color-border)] text-center">
        <Stat
          label="Monthly equiv."
          value={dollars(sub.monthly_equivalent_cents)}
          small
        />
        <Stat
          label={sub.cancel_at_period_end ? 'Ends' : 'Renews'}
          value={sub.current_period_end ? formatDate(sub.current_period_end) : '—'}
          small
        />
      </div>
    </Card>
  );
}

function Stat({ label, value, small }: { label: string; value: string; small?: boolean }) {
  return (
    <div className="px-4 py-3">
      <div
        className={`uppercase tracking-wide text-[var(--color-foreground-subtle)] ${small ? 'text-[10px]' : 'text-xs'}`}
      >
        {label}
      </div>
      <div className={`mt-0.5 font-semibold tabular-nums ${small ? '' : 'text-lg'}`}>{value}</div>
    </div>
  );
}
